const NAMED: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

/**
 * Decode the handful of HTML entities that leak into plain-text listing
 * fields (short descriptions pasted from a rich editor carry `&nbsp;`,
 * `&amp;`…). These fields are rendered as text, never as HTML, so a literal
 * `&nbsp;` showed on the card. Safe on SSR: no DOM needed.
 */
export function decodeHtmlEntities(text: string | undefined | null): string {
  if (!text) return "";
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === "#") {
      const code =
        entity[1] === "x" || entity[1] === "X"
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
      // fromCodePoint throws above U+10FFFF; leave such junk as-is.
      return Number.isFinite(code) && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : match;
    }
    return NAMED[entity.toLowerCase()] ?? match;
  });
}

const NAMED_ANY_CASE = new RegExp(`&(${Object.keys(NAMED).join("|")});`, "gi");

/**
 * Lowercase the names of the entities above (`&NBSP;` → `&nbsp;`) in Markdown
 * about to be parsed. HTML5 has no `&NBSP;` or `&APOS;`, so CommonMark leaves
 * those literal, yet pasted text carries them. Fixing only the case leaves
 * the decoding to CommonMark, which keeps `\&nbsp;` escaped and doesn't
 * double-decode `&amp;amp;`, and keeps the length, so source offsets hold.
 */
export function normalizeEntityCase(markdown: string): string {
  return markdown.replace(NAMED_ANY_CASE, (_, name: string) => `&${name.toLowerCase()};`);
}
